import { apiFetch } from '@/shared/api/client'
import { shouldUseDelmanMock } from '@/features/delman/api/delmanApiMode'

export type ApiKitchenTransferLine = {
  item_type?: string
  product_name?: string
  material_name?: string
  production_date?: string
  uom?: string
  qty_shipped: number
  qty_received?: number
}

export type ApiKitchenTransferDrop = {
  id: string
  target_kitchen_name: string
  source_kitchen_name: string
  sort_order: number
  status: string
  receiving_status?: string
  handover_qr_token?: string
  handover_qr_expires_at?: string
  lines: ApiKitchenTransferLine[]
}

export type ApiKitchenTransferTrip = {
  id: string
  trip_code: string
  source_kitchen_name: string
  delman_name: string
  date: string
  status: string
  transfers: ApiKitchenTransferDrop[]
}

export async function fetchKitchenTransferTrips(date: string): Promise<ApiKitchenTransferTrip[]> {
  if (shouldUseDelmanMock()) return []
  const res = await apiFetch<{ items: ApiKitchenTransferTrip[] }>(
    `/kitchen-transfer-trips?date=${encodeURIComponent(date)}`,
  )
  return res.items ?? []
}

export async function fetchKitchenTransferTrip(tripId: string): Promise<ApiKitchenTransferTrip> {
  return apiFetch<ApiKitchenTransferTrip>(`/kitchen-transfer-trips/${encodeURIComponent(tripId)}`)
}

export async function generateKitchenTransferQr(
  transferId: string,
  deviceId: string,
  options?: { force?: boolean },
): Promise<{ token: string; expires_at: string; reused?: boolean }> {
  if (shouldUseDelmanMock()) {
    const expires = new Date(Date.now() + 15 * 60 * 1000)
    return {
      token: `KTH-${transferId.slice(0, 8)}-MOCK`,
      expires_at: expires.toISOString(),
      reused: false,
    }
  }
  return apiFetch(`/kitchen-transfers/${encodeURIComponent(transferId)}/generate-qr`, {
    method: 'POST',
    body: JSON.stringify({ device_id: deviceId, force: options?.force ?? false }),
  })
}

export async function completeKitchenTransferTrip(
  tripId: string,
  deviceId: string,
  pin: string,
): Promise<{ completed_at: string; transit_remainder_qty: number }> {
  if (shouldUseDelmanMock()) {
    return { completed_at: new Date().toISOString(), transit_remainder_qty: 0 }
  }
  return apiFetch(`/kitchen-transfer-trips/${encodeURIComponent(tripId)}/complete`, {
    method: 'PATCH',
    body: JSON.stringify({ device_id: deviceId, pin }),
  })
}

export function formatTransferLineSummary(lines: ApiKitchenTransferLine[]): string {
  const productQty = lines
    .filter((l) => (l.item_type ?? 'product') === 'product')
    .reduce((s, l) => s + l.qty_shipped, 0)
  const materialQty = lines
    .filter((l) => l.item_type === 'material')
    .reduce((s, l) => s + l.qty_shipped, 0)
  const parts: string[] = []
  if (productQty > 0) parts.push(`${productQty} pcs`)
  if (materialQty > 0) parts.push(`${materialQty} bahan`)
  return parts.length > 0 ? parts.join(' · ') : '0 baris'
}

export function formatTransferLineLabel(line: ApiKitchenTransferLine): string {
  if (line.item_type === 'material') {
    return `${line.material_name} (${line.uom}) · ${line.qty_shipped}`
  }
  return `${line.product_name} · ${line.production_date} · ${line.qty_shipped} pcs`
}

export function isKitchenTransferQrExpired(expiresAt: string | undefined): boolean {
  if (!expiresAt) return true
  return new Date(expiresAt).getTime() <= Date.now()
}

export function formatKitchenTransferQrExpiry(expiresAt: string): string {
  const d = new Date(expiresAt)
  const h = String(d.getHours()).padStart(2, '0')
  const m = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${h}:${m}:${s}`
}
